"use client";

import { Icon } from "@zapier/zinnia-icons";

import styles from "./FolderPickerModal.module.css";

interface NewFolderRowProps {
  /** Depth of the *new folder itself* (parent.depth + 1, or 0 at root). */
  depth: number;
  onCommit: (name: string) => void;
  onCancel: () => void;
  placeholder: string;
}

/**
 * Inline editing row for a folder being created. Visually identical to
 * `FolderRow` (chevron spacer + folder icon + 36px row height + same
 * indentation math) so committed folders don't visually jump when their
 * input is replaced by the real row. Keeps the same editing
 * state exactly: leading folder icon, "Untitled" placeholder, autoFocus,
 * and commit on Enter/blur. Empty + blur cancels (rather than creating
 * an "Untitled" folder) so accidental clicks don't leave debris behind.
 */
export function NewFolderRow({
  depth,
  onCommit,
  onCancel,
  placeholder,
}: NewFolderRowProps) {
  const indent = depth * 24;
  return (
    <div
      className={styles.folderRow}
      style={{ paddingInlineStart: indent + 8 }}
    >
      {/* Chevron column spacer so the folder icon aligns with sibling rows
          that have chevrons. The new folder is a leaf at the moment of
          creation so it never has its own chevron. */}
      <span className={styles.chevronSpacer} aria-hidden />
      <span className={styles.folderIcon} aria-hidden>
        <Icon name="navFolder" size="1.5em" />
      </span>
      <input
        // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the new-folder name input when it appears
        autoFocus
        className={styles.newFolderInput}
        placeholder={placeholder}
        defaultValue=""
        onBlur={(event) => onCommit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            onCommit((event.target as HTMLInputElement).value);
          } else if (event.key === "Escape") {
            onCancel();
          }
        }}
      />
    </div>
  );
}
